import { cn } from '@up2stay/up2promo-ui';

/** Uppercase iOS-style section label above a grouped card. */
export const SETTINGS_GROUP_LABEL_CLASS =
  'px-1 text-[11px] font-semibold uppercase leading-none tracking-[0.6px] text-muted-foreground';

/** Small low-contrast explainer below a grouped card (never inside rows). */
export const SETTINGS_GROUP_FOOTER_CLASS =
  'max-w-prose px-1 text-[12px] leading-snug text-muted-foreground/85';

export const SETTINGS_GROUP_SURFACE_CLASS =
  'overflow-hidden rounded-[var(--r-settings-group,12px)] border border-border bg-card shadow-[var(--shadow-card)]';

export const SETTINGS_GROUP_ROWS_CLASS = 'flex flex-col';

export const SETTINGS_GROUP_ROW_CLASS = 'px-4 py-3.5 sm:px-5';

/**
 * Inset hairline between rows — equal horizontal inset matching row `px-4` /
 * `sm:px-5` (space on left and right).
 * Uses shared chrome `--border` / `border-border` (no settings-only token).
 */
export const SETTINGS_GROUP_ROW_SEPARATOR_CLASS =
  'mx-4 border-t border-border sm:mx-5 [border-top-width:1px]';

type SettingsGroupProps = {
  children: React.ReactNode;
  className?: string;
  tone?: 'default' | 'danger';
};

export function SettingsGroup({
  children,
  className,
  tone = 'default',
}: SettingsGroupProps) {
  return (
    <div
      className={cn(
        SETTINGS_GROUP_SURFACE_CLASS,
        tone === 'danger' && 'border border-destructive/25',
        className,
      )}
    >
      {children}
    </div>
  );
}

type SettingsGroupLabelProps = {
  id?: string;
  children: React.ReactNode;
  className?: string;
};

export function SettingsGroupLabel({
  id,
  children,
  className,
}: SettingsGroupLabelProps) {
  return (
    <h2 id={id} className={cn(SETTINGS_GROUP_LABEL_CLASS, className)}>
      {children}
    </h2>
  );
}

type SettingsGroupFooterProps = {
  children: React.ReactNode;
  className?: string;
};

export function SettingsGroupFooter({
  children,
  className,
}: SettingsGroupFooterProps) {
  return (
    <p className={cn(SETTINGS_GROUP_FOOTER_CLASS, className)}>{children}</p>
  );
}

type SettingsGroupRowsProps = {
  children: React.ReactNode;
  className?: string;
};

export function SettingsGroupRows({
  children,
  className,
}: SettingsGroupRowsProps) {
  return (
    <div className={cn(SETTINGS_GROUP_ROWS_CLASS, className)}>{children}</div>
  );
}

type SettingsGroupRowProps = {
  children: React.ReactNode;
  className?: string;
  /** When true, draw an inset hairline above this row. */
  separated?: boolean;
};

export function SettingsGroupRow({
  children,
  className,
  separated = false,
}: SettingsGroupRowProps) {
  return (
    <div className="flex flex-col">
      {separated ? (
        <div className={SETTINGS_GROUP_ROW_SEPARATOR_CLASS} aria-hidden />
      ) : null}
      <div className={cn(SETTINGS_GROUP_ROW_CLASS, className)}>{children}</div>
    </div>
  );
}
